import type {
  HeadToHeadResponse,
  HistoricalMatchDetailResponse,
  TeamHistoryResponse,
} from '@ballpeek/api/client'
import type { ZodType } from 'zod'
import type { HeadToHeadSearch, TeamHistorySearch } from './search-schema'

import {
  headToHeadResponseSchema,
  historicalMatchDetailResponseSchema,
  publicRequestIdSchema,
  teamHistoryResponseSchema,
} from '@ballpeek/api/client'
import * as Sentry from '@sentry/react'

import { getApiBaseUrl } from '~/lib/api-client'
import {
  PublicFootballRequestError,
  readPublicFootballResponse,
} from '~/lib/public-football-read'

export type HistoricalMatchLoadResult
  = | { kind: 'canonical', matchId: string }
    | { kind: 'historical', data: HistoricalMatchDetailResponse }

function scopeQuery(search: Pick<TeamHistorySearch, 'competition' | 'season' | 'from' | 'to' | 'page'>) {
  const query = new URLSearchParams()
  if (search.competition !== undefined && search.season !== undefined) {
    query.set('competitionCode', search.competition)
    query.set('season', String(search.season))
  }
  if (search.from !== undefined)
    query.set('from', search.from)
  if (search.to !== undefined)
    query.set('to', search.to)
  if (search.page > 1)
    query.set('page', String(search.page))
  query.set('pageSize', '20')
  return query
}

async function historyFetch(path: string, redirect: RequestRedirect = 'follow'): Promise<Response> {
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    headers: { Accept: 'application/json' },
    redirect,
  })
  if (!response.ok && response.status !== 307) {
    Sentry.addBreadcrumb({
      category: 'api_call',
      message: `Football history request failed: ${response.status}`,
      level: 'error',
      data: {
        api_request_id: response.headers.get('X-Request-Id'),
        path,
        status: response.status,
      },
    })
  }
  return response
}

async function requestJson<T>(path: string, schema: ZodType<T>): Promise<T> {
  const response = await historyFetch(path)
  return readPublicFootballResponse(response, schema, 'Football history request failed')
}

export function listTeamHistory(teamId: string, search: TeamHistorySearch): Promise<TeamHistoryResponse> {
  const query = scopeQuery(search)
  return requestJson(
    `/api/football/teams/${encodeURIComponent(teamId)}/history?${query.toString()}`,
    teamHistoryResponseSchema,
  )
}

export async function getHistoricalMatch(historicalMatchId: string): Promise<HistoricalMatchLoadResult> {
  const response = await historyFetch(
    `/api/football/history/matches/${encodeURIComponent(historicalMatchId)}`,
    'manual',
  )
  if (response.status === 307) {
    const location = response.headers.get('Location')
    const prefix = '/api/football/matches/'
    if (location?.startsWith(prefix)) {
      return {
        kind: 'canonical',
        matchId: decodeURIComponent(location.slice(prefix.length)),
      }
    }
    const requestId = publicRequestIdSchema.safeParse(response.headers.get('X-Request-Id'))
    throw new PublicFootballRequestError({
      message: 'Football history redirect was invalid',
      status: 503,
      requestId: requestId.success ? requestId.data : null,
      publicError: {
        success: false,
        error: {
          name: 'Error',
          issues: [{ code: 'read_model_unavailable', path: ['Location'] }],
        },
      },
    })
  }
  const data = await readPublicFootballResponse(
    response,
    historicalMatchDetailResponseSchema,
    'Football history request failed',
  )
  return { kind: 'historical', data }
}

export function getHeadToHead(search: HeadToHeadSearch): Promise<HeadToHeadResponse> {
  const query = scopeQuery(search)
  query.set('teamAId', search.teamA)
  query.set('teamBId', search.teamB)
  return requestJson(`/api/football/head-to-head?${query.toString()}`, headToHeadResponseSchema)
}
